import { createPortal } from "react-dom";

import { useLivestreamChat } from "../../context/LivestreamChatProvider";
import {
  TWITCH_PLAYER_IFRAME_ALLOW,
  TWITCH_PLAYER_IFRAME_SANDBOX,
} from "../../services/twitchServices";
import { usePopoutDragResize, type ResizeCorner } from "./hooks/usePopoutDragResize";

const RESIZE_HANDLES: Array<{ corner: ResizeCorner; cursor: string; position: string }> = [
  { corner: "nw", cursor: "cursor-nwse-resize", position: "top-0 left-0" },
  { corner: "ne", cursor: "cursor-nesw-resize", position: "top-0 right-0" },
  { corner: "sw", cursor: "cursor-nesw-resize", position: "bottom-0 left-0" },
  { corner: "se", cursor: "cursor-nwse-resize", position: "right-0 bottom-0" },
];

interface LivestreamPopoutProps {
  embedSrc: string;
  chatSrc?: string;
  title: string;
  dockLabel: string;
  showChatLabel: string;
  hideChatLabel: string;
  onDock: () => void;
  allowChat?: boolean;
  offsetIndex?: number;
}

export const LivestreamPopout = ({
  embedSrc,
  chatSrc,
  title,
  dockLabel,
  showChatLabel,
  hideChatLabel,
  onDock,
  allowChat = true,
  offsetIndex = 0,
}: LivestreamPopoutProps) => {
  const { chatOpen, toggleChat } = useLivestreamChat();
  const popoutChatOpen = allowChat && chatOpen && Boolean(chatSrc);
  const {
    popoutRef,
    playerSrc,
    layout,
    dragging,
    resizing,
    playerWidth,
    playerHeight,
    chatWidth,
    handleDragPointerDown,
    handleResizePointerDown,
    stopToolbarPointer,
  } = usePopoutDragResize({
    embedSrc,
    chatOpen: popoutChatOpen,
    offsetIndex,
  });

  return createPortal(
    <aside
      ref={popoutRef}
      className={[
        "fixed z-[1150] overflow-hidden border border-[rgba(44,137,225,0.45)] rounded-[0.75rem] bg-[#0b0b12] shadow-[0_16px_48px_rgba(0,0,0,0.45)] max-[900px]:hidden",
        resizing && "select-none",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ left: layout.x, top: layout.y, width: layout.width }}
      aria-label={title}
    >
      <div
        className={`flex items-center justify-between gap-[0.5rem] p-[0.45rem_0.55rem] border-b border-[rgba(44,137,225,0.25)] bg-[rgba(15,23,42,0.96)] touch-none select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        onPointerDown={handleDragPointerDown}
      >
        <span className="overflow-hidden text-[#e9d5ff] text-[0.72rem] font-bold tracking-[0.06em] text-ellipsis uppercase whitespace-nowrap">
          {title}
        </span>
        <div className="flex items-center gap-[0.35rem] shrink-0">
          {allowChat ? (
            <button
              type="button"
              className="shrink-0 border border-[rgba(148,163,184,0.35)] rounded-[0.45rem] bg-[rgba(15,23,42,0.8)] text-[#e8e8ef] text-[0.72rem] font-semibold p-[0.25rem_0.5rem] cursor-pointer hover:border-[rgba(123,147,255,0.45)] hover:bg-[rgba(123,147,255,0.12)]"
              onClick={toggleChat}
              onPointerDown={stopToolbarPointer}
              aria-pressed={chatOpen}
            >
              {chatOpen ? hideChatLabel : showChatLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="shrink-0 border border-[rgba(145,70,255,0.45)] rounded-[0.45rem] bg-[rgba(145,70,255,0.18)] text-[#e9d5ff] text-[0.72rem] font-semibold p-[0.25rem_0.5rem] cursor-pointer hover:bg-[rgba(145,70,255,0.28)]"
            onClick={onDock}
            onPointerDown={stopToolbarPointer}
          >
            {dockLabel}
          </button>
        </div>
      </div>

      <div className="flex min-w-0">
        <div
          className="relative flex-[0_0_auto] min-w-0 bg-black"
          style={{ width: playerWidth, height: playerHeight }}
        >
          <iframe
            sandbox={TWITCH_PLAYER_IFRAME_SANDBOX}
            src={playerSrc}
            title={title}
            allow={TWITCH_PLAYER_IFRAME_ALLOW}
            className="block w-full h-full border-none"
          />
        </div>
        {popoutChatOpen ? (
          <div
            className="flex-[0_0_auto] min-w-0 flex border-l border-[rgba(44,137,225,0.25)] bg-[#0e0e10]"
            style={{ width: chatWidth }}
          >
            <iframe
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              src={chatSrc}
              title={`${title} chat`}
              className="block h-full w-full border-none"
            />
          </div>
        ) : null}
      </div>

      {RESIZE_HANDLES.map(({ corner, cursor, position }) => (
        <div
          key={corner}
          className={`absolute z-[4] w-[0.85rem] h-[0.85rem] touch-none ${cursor} ${position} ${
            corner === "se"
              ? "after:absolute after:content-[''] after:right-[0.15rem] after:bottom-[0.15rem] after:w-[0.45rem] after:h-[0.45rem] after:border-r-2 after:border-b-2 after:border-[rgba(148,163,184,0.55)] after:rounded-br-[0.15rem] after:pointer-events-none"
              : ""
          }`}
          onPointerDown={handleResizePointerDown(corner)}
          aria-hidden
        />
      ))}
    </aside>,
    document.body,
  );
};
